import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { loginUser, signupUser, sendGoogleIdToken } from '../../api';
import dashboardPreview from '../../assets/images/dashboard-preview.jpg';
import analyticsPreview from '../../assets/images/analytics-preview.jpg';
import customizationPreview from '../../assets/images/customization-preview.png';

const carouselImages = [
  {
    url: dashboardPreview,
    title: 'Powerful Dashboard',
    description: 'Manage all your chatbots from a single, intuitive interface',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
      </svg>
    )
  },
  {
    url: analyticsPreview,
    title: 'Real-time Analytics',
    description: 'Get detailed insights into your chatbot performance and user interactions',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    )
  },
  {
    url: customizationPreview,
    title: 'Easy Customization',
    description: 'Customize your chatbots with our powerful yet simple tools',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>
    )
  }
];

export default function Login() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideDirection, setSlideDirection] = useState('right');

  const goToSlide = (index) => {
    setSlideDirection(index > currentSlide ? 'right' : 'left');
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setSlideDirection('right');
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setSlideDirection('left');
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: async (response) => {
            try {
              const backendResponse = await sendGoogleIdToken(response.credential);
              if (backendResponse && backendResponse.status === 'success') {
                navigate('/dashboard');
              } else {
                setError(backendResponse?.message || 'Google login failed.');
              }
            } catch (err) {
              setError(err.error || err.message || 'Google sign-in failed.');
            }
          },
        });
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    const renderGoogleButton = () => {
      if (
        window.google &&
        window.google.accounts &&
        window.google.accounts.id &&
        document.getElementById('google-signin-button')
      ) {
        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-button'),
          { theme: 'outline', size: 'large', width: '100%' }
        );
      }
    };

    renderGoogleButton();
    const timeoutId = setTimeout(renderGoogleButton, 1000);

    return () => clearTimeout(timeoutId);
  }, []);

  const handleTabClick = (tab) => {
    if (activeTab === tab) return;
    setActiveTab(tab);
    setFormData({ name: '', email: '', password: '' });
    setError('');
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      let response;
      if (activeTab === 'login') {
        response = await loginUser(formData.email, formData.password);
      } else {
        response = await signupUser(formData.name, formData.email, formData.password);
      }
      if (response && (response.token || response.status === 'success')) {
        navigate('/dashboard');
      } else {
        setError(response.message || 'Login/Signup failed.');
      }
    } catch (err) {
      setError(err.error || err.message || 'An error occurred.');
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 py-12 relative">
        <div className="max-w-md w-full mx-auto bg-gray-50 rounded-2xl shadow-xl p-10">
          {/* Logo */}
          <div className="flex items-center mb-12">
            <div className="w-10 h-10 flex items-center justify-center bg-transparent">
              <img 
                src="/favicon/favicon2.jpg" 
                alt="Linkforce AI Logo" 
                className="object-contain"
                style={{
                  mixBlendMode: 'multiply',
                  filter: 'contrast(1.1) brightness(1.1)'
                }}
              />
            </div>
            <div className="ml-4">
              <h1 className="text-2xl font-bold bg-blue-500 from-indigo-600 to-indigo-800 bg-clip-text text-transparent">
              Linkforce AI
            </h1>
              <p className="text-sm text-gray-600">Professional Chat Solutions</p>
            </div>
          </div>

          {/* Header */}
          <div className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {activeTab === 'login' ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-gray-600">
              {activeTab === 'login' 
                ? 'Sign in to your account to continue' 
                : 'Get started with your free account'}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex mb-8 bg-gray-50 rounded-lg p-1 shadow-sm">
            <button
              onClick={() => handleTabClick('login')}
              className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === 'login'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => handleTabClick('signup')}
              className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
                activeTab === 'signup'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {activeTab === 'signup' && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required={activeTab === 'signup'}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors text-gray-700 bg-white shadow-sm"
                  placeholder="Enter your full name"
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors text-gray-700 bg-white shadow-sm"
                placeholder="Enter your email address"
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <label className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-indigo-800 font-medium">
                  Forgot Password?
                </Link>
              </div>
              <div className="relative">
              <input
                  type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors text-gray-700 bg-white shadow-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg text-sm border border-red-100">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-blue-500 from-indigo-600 to-indigo-800 text-white py-3.5 rounded-lg hover:from-indigo-700 hover:to-indigo-900 transition-all duration-200 font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              {activeTab === 'login' ? 'Sign In' : 'Create Account'}
            </button>

            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div
                id="google-signin-button"
                className="flex items-center justify-center"
              />
            </div>
          </form>
        </div>
      </div>

      {/* Right Panel - Image Carousel */}
      <div className="hidden lg:block lg:w-1/2 bg-gray-50">
        <div className="relative h-[800px] rounded-2xl overflow-hidden shadow-2xl bg-white border-2 border-gray-300 border-solid">
          {/* Carousel Container */}
          <div className="absolute inset-0">
            {carouselImages.map((slide, index) => (
              <div
              key={index} 
                className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                  currentSlide === index
                    ? 'opacity-100 translate-x-0 scale-100'
                    : slideDirection === 'right'
                    ? 'opacity-0 translate-x-full scale-95'
                    : 'opacity-0 -translate-x-full scale-95'
                }`}
              >
                <div className="relative h-full rounded-xl overflow-hidden group">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10 bg-gradient-to-br from-indigo-500 to-purple-500" 
              style={{
                         backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'20\' height=\'20\' viewBox=\'0 0 20 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%234f46e5\' fill-opacity=\'0.2\' fill-rule=\'evenodd\'%3E%3Ccircle cx=\'3\' cy=\'3\' r=\'1\'/%3E%3C/g%3E%3C/svg%3E")',
                         backgroundSize: '20px 20px'
                       }}>
                  </div>

                  {/* Main Image */}
                  <img 
                    src={slide.url} 
                    alt={slide.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
                  />

                  {/* Content Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
                    <div className="absolute bottom-0 left-0 right-0 p-10">
                      {/* Icon with Glow */}
                      <div className="mb-6 inline-block p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                        <div className="text-white">
                          {slide.icon}
                        </div>
                      </div>

                      {/* Text Content */}
                      <h2 className="text-4xl font-bold text-white mb-4 transform transition-all duration-300 group-hover:translate-x-2">
                        {slide.title}
                      </h2>
                      <p className="text-lg text-gray-200 max-w-lg transform transition-all duration-300 group-hover:translate-x-2">
                        {slide.description}
                      </p>

                      {/* Feature Pills */}
                      <div className="flex gap-3 mt-6">
                        <span className="px-4 py-2 bg-white/10 rounded-full text-sm text-white backdrop-blur-sm">
                          Easy Setup
                        </span>
                        <span className="px-4 py-2 bg-white/10 rounded-full text-sm text-white backdrop-blur-sm">
                          24/7 Support
                        </span>
                        <span className="px-4 py-2 bg-white/10 rounded-full text-sm text-white backdrop-blur-sm">
                          Real-time Updates
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="absolute bottom-20 left-10 right-10 h-1 bg-gray-200/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all duration-500 ease-out"
              style={{ width: `${((currentSlide + 1) / carouselImages.length) * 100}%` }}
            />
          </div>

          {/* Navigation Dots */}
          <div className="absolute bottom-8 left-10 flex items-center space-x-3 z-20">
            {carouselImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 flex items-center ${
                  currentSlide === index
                    ? 'opacity-100'
                    : 'opacity-50 hover:opacity-75'
                }`}
              >
                <span className={`w-2 h-2 rounded-full bg-white transition-all duration-300 ${
                  currentSlide === index ? 'w-8' : 'w-2'
                }`} />
              </button>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-6 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full bg-black/20 hover:bg-black/40 flex items-center justify-center text-white transition-all duration-300 backdrop-blur-sm hover:scale-110"
            aria-label="Previous slide"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-6 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full bg-black/20 hover:bg-black/40 flex items-center justify-center text-white transition-all duration-300 backdrop-blur-sm hover:scale-110"
            aria-label="Next slide"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Slide Counter */}
          <div className="absolute top-8 right-10 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <span className="text-white font-medium">
              {currentSlide + 1} / {carouselImages.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
